import { useState } from 'react'
import { assetUrl } from '../helper_functions/utilities'
import PersonalChampionsIconWithFallBack from '../shared/PersonalChampionIcons'
import { PiInfinityBold } from "react-icons/pi"
import HorizontalScrollArea from '../shared/HorizontalScrollArea'
import { buttonFocusRingClass, neutralButtonOnZincSurfaceClass } from '../shared/buttonStyles'

const filterOptions = [
    { label: "ALL", value: "" },
    { label: "RANKED SOLO", value: 420 },
    { label: "FLEX", value: 440 },
    { label: "NORMAL", value: 400 },
    { label: "QUICK PLAY", value: 490 },
]

function formatSrPerformanceChampionName(championName) {
    if (championName === 'Shadow Assassin') {
        return 'S. Assassin'
    }

    return championName
}

const PersonalChampionPerformance = ({ personalChampionPerformances }) => {
    const [performanceFilter, setPerformanceFilter] = useState("")

    function consolidateStatsByChampionAndYear(stats) {
        return stats?.reduce((accumulator, current) => {
            const existingIndex = accumulator.findIndex((champStat) => (
                champStat.champion_id === current.champion_id && champStat.year === current.year
            ))

            if (existingIndex !== -1) {
                accumulator[existingIndex].games += current.games
                accumulator[existingIndex].wins += current.wins
                accumulator[existingIndex].losses += current.losses
                accumulator[existingIndex].kills += current.kills
                accumulator[existingIndex].deaths += current.deaths
                accumulator[existingIndex].assists += current.assists
                accumulator[existingIndex].cs += current.cs
                accumulator[existingIndex].csm += current.csm
                accumulator[existingIndex].total_minutes += current.total_minutes
            } else {
                accumulator.push({ ...current })
            }
            return accumulator
        }, [])
    }

    const filteredPerformances = (performanceFilter === ""
        ? consolidateStatsByChampionAndYear(personalChampionPerformances)
        : personalChampionPerformances.filter((performance) => performance.queue_id === performanceFilter))

    const ladderSelectedButtonClassName = '!border-zinc-700 !bg-cyan-900/45 !text-cyan-200 hover:!border-zinc-700 hover:!bg-cyan-900/45 hover:!text-cyan-200'
    const filterButtonInactiveClass = `inline-flex h-8 items-center justify-center rounded-md px-3 text-[11px] font-semibold uppercase tracking-[0.16em] whitespace-nowrap transition-colors snap-start ${buttonFocusRingClass} ${neutralButtonOnZincSurfaceClass}`
    const filterButtonActiveClass = `inline-flex h-8 items-center justify-center rounded-md px-3 text-[11px] font-semibold uppercase tracking-[0.16em] whitespace-nowrap transition-colors snap-start ${buttonFocusRingClass} ${ladderSelectedButtonClassName}`
    const getFilterButtonClass = (isActive) => (isActive ? filterButtonActiveClass : filterButtonInactiveClass)

    return (
        <section className="mb-3 w-full rounded-xl border border-zinc-800 bg-zinc-950 px-3.5 pt-3 pb-4 text-zinc-200 shadow-[0_18px_36px_-28px_rgba(0,0,0,0.95)]">
            <div className="-mx-3.5 -mt-3 mb-3 border-b border-zinc-800 bg-zinc-900/65 px-3.5 py-3 text-left">
                <h2 className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">SUMMONER'S RIFT PERFORMANCE</h2>
            </div>

            <HorizontalScrollArea
                className="mb-3"
                contentClassName="flex gap-2"
                railClassName="bg-zinc-900/78 group-hover:bg-zinc-800/74"
                thumbClassName="bg-zinc-800/72 group-hover:bg-zinc-600/84"
                viewportClassName="snap-x snap-mandatory scroll-auto pb-1"
            >
                    {filterOptions.map((option) => (
                        <button
                            key={option.label}
                            type="button"
                            className={getFilterButtonClass(performanceFilter === option.value)}
                            onClick={() => setPerformanceFilter(option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
            </HorizontalScrollArea>

            <div className='flex flex-col'>
                {personalChampionPerformances?.length === 0 && (
                    <div className='rounded-md border border-amber-400/50 bg-amber-300/10 px-3 py-3 text-sm font-medium text-amber-100 text-center'>
                        No information yet. Try updating.
                    </div>
                )}
                {filteredPerformances?.map((championPerformance, index) => {
                    const deaths = championPerformance?.deaths || 0
                    const kills = championPerformance?.kills || 0
                    const assists = championPerformance?.assists || 0
                    const games = championPerformance?.games || 0
                    const totalMinutes = championPerformance?.total_minutes || 0
                    const kda = deaths === 0 ? 0 : (kills + assists) / deaths
                    const showInfinity = deaths === 0 && (kills + assists) > 0
                    const averageCs = games > 0 ? Math.round((championPerformance?.cs || 0) / games) : 0
                    const csm = totalMinutes > 0 ? ((championPerformance?.cs || 0) / totalMinutes).toFixed(1) : "0.0"
                    const kAvg = games > 0 ? (kills / games).toFixed(1) : "0.0"
                    const dAvg = games > 0 ? (deaths / games).toFixed(1) : "0.0"
                    const aAvg = games > 0 ? (assists / games).toFixed(1) : "0.0"
                    const wr = games > 0 ? ((championPerformance?.wins / games) * 100).toFixed(1) : "0.0"

                    return (
                    <article key={index} className='grid grid-cols-[minmax(0,1fr)_108px_70px] items-center gap-x-2 overflow-visible border-b border-zinc-800/80 px-2 py-2 text-white last:border-b-0 first:pt-1 last:pb-1'>
                            <div className='flex min-w-0 items-center gap-2 overflow-visible'>
                                <div className='relative overflow-hidden h-10 w-10 rounded-full border border-zinc-600 shrink-0'>
                                    <PersonalChampionsIconWithFallBack src={assetUrl(`/champion_icons/${championPerformance?.name_id}.png`)} alt={`${championPerformance?.name} icon`} />
                                </div>
                                <div className='min-w-0 overflow-visible'>
                                    <div className='relative z-[1] whitespace-nowrap text-xs font-semibold'>{formatSrPerformanceChampionName(championPerformance?.name)}</div>
                                    {showInfinity ? (
                                        <p className='flex items-center text-sm font-bold leading-tight text-cyan-500'>
                                            <PiInfinityBold className="h-[18px] w-[18px] shrink-0 sm:h-[18px] sm:w-[18px]" strokeWidth={6} />
                                            <span className='ml-1 text-[10px] text-zinc-300 mt-0.5'>KDA</span>
                                        </p>
                                    ) : (
                                        <p className='text-sm font-bold leading-tight'>
                                            <span className={kda > 5.0 ? "text-cyan-500" : ""}>{kda.toFixed(1)}</span>
                                            <span className='text-[10px] text-zinc-300 ml-1'>KDA</span>
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className='w-[108px] justify-self-end text-[11px] leading-tight text-right text-zinc-300 whitespace-nowrap tabular-nums'>
                                <p>{averageCs} CS ({csm})</p>
                                <p>{kAvg} / {dAvg} / {aAvg}</p>
                            </div>
                            <div className='w-[70px] justify-self-end text-[11px] leading-tight text-right whitespace-nowrap tabular-nums'>
                                <p>{wr}% WR</p>
                                <p className='text-zinc-300'>{games} game(s)</p>
                            </div>
                        </article>
                    )
                })}
            </div>
        </section>
    )
}

export default PersonalChampionPerformance
